import React, { Component } from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import { Redirect } from 'react-router-dom';

import { makeSignupReasonFromLocationQuery } from 'client/pages/Onboarding/index';
import {
  SIGNUP_REASON_TOUR,
  SIGNUP_REASON_BOOKMARK,
  SIGNUP_REASON_APARTMENT_LOCATOR_INVITE,
} from 'common/onboardingConstants';
import { SR_API_ACTION_LOGIN, SR_API_ACTION_ASSIGN_LEAD_APARTMENT_LOCATOR } from 'api/actionTypes';
import { hasRoleBuyerSideAgent, USER_ROLE_ANONYMOUS } from 'common/userRoles';
import { isListingTenantScreening } from 'common/listingHelpers';
import { SCREEN_ID_LOGIN } from 'client/constants/screenIds';

import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import withApiState from 'client/helpers/withApiState';
import branch from 'client/core/branch';
import { getUserLandingPage } from 'client/helpers/allowedRoles';
import postWithState from 'client/actions/postWithState';
import firstWordCapitalized from 'common/util/firstWordCapitalized';

import SrLink from 'client/components/SrLink/SrLink';
import SrLoginForm from 'client/components/SrLoginForm';
import SrLogo from 'client/components/SrLogo';
import SrEnablePushNotificationsModal from 'client/components/SrEnablePushNotificationsModal';

import AuthActions from 'client/actions/Auth';
import TourActions from 'client/actions/Tour';
import UserActions from 'client/actions/User';
import ApartmentLocatorActions from 'client/actions/ApartmentLocator';
import HouseholdActions from 'client/actions/Household';
import LandlordActions from 'client/actions/Landlord';
import ListingActions from 'client/actions/Listing';

const SIGNUP_REASON_TO_LOGIN_HERO_MESSAGE = {
  [SIGNUP_REASON_TOUR]: 'tour this home',
  [SIGNUP_REASON_BOOKMARK]: 'save homes',
};

@withTransitionHook
@withApiState({
  loginApiState: {
    action: SR_API_ACTION_LOGIN,
  },
})
@withScreenId(SCREEN_ID_LOGIN)
@withScrollToTop
@branch({
  currentUser: ['currentUser'],
  tours: ['tours'],
  isMobileApp: ['mobileApp', 'isMobileApp'],
  isPartnerApp: ['mobileApp', 'isPartnerApp'],
  askedPushNotificationPermission: ['mobileApp', 'askedPushNotificationPermission'],
  apartmentLocators: ['apartmentLocators'],
  listing: ['listing'],
})
class LoginPage extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Login | Sunroom Rentals',
    section: 'Login',
    // canonical: '/login',
    // keywords: 'some, keywords',
    // description: 'Site description here.',
  });

  static propTypes = {
    currentUser: PropTypes.object.isRequired,
    tours: PropTypes.array,
    loginApiState: PropTypes.object.isRequired,
    isMobileApp: PropTypes.bool.isRequired,
    isPartnerApp: PropTypes.bool.isRequired,
    askedPushNotificationPermission: PropTypes.bool.isRequired,
    apartmentLocators: PropTypes.array,
    history: PropTypes.object.isRequired,
  };

  static fetchData(state, inject) {
    const locationQuery = queryString.parse(inject.location.search);

    if (locationQuery.listingId) {
      return ListingActions.getById(inject, locationQuery.listingId);
    }
  }

  constructor(props) {
    super(props);
    const locationQuery = queryString.parse(props.location.search);

    this.state = {
      isSubmitting: false,
      showEnablePushNotificationModal: false,
      hasNextActionAfterLogin: !!(
        locationQuery.tourRequested ||
        locationQuery.bookmarkRequested ||
        locationQuery.invitedBy ||
        (locationQuery.ownerInvited && locationQuery.listingId) ||
        (locationQuery.wantsToApply && locationQuery.listingId) ||
        (locationQuery.selfTouring && locationQuery.listingId) ||
        (locationQuery.startPrequal && locationQuery.listingId) ||
        (locationQuery.joinWaitlist && locationQuery.listingId)
      ),
    };
    this.onFormSubmit = this.onFormSubmit.bind(this);
    this._onNextLoginAction = this._onNextLoginAction.bind(this);
  }

  componentDidMount() {
    const { location, dispatch } = this.props;
    const locationQuery = queryString.parse(location.search);

    if (locationQuery.invitedBy) {
      dispatch(ApartmentLocatorActions.getApartmentLocatorsList);
    }
  }

  async onFormSubmit(formValues) {
    const { dispatch, isMobileApp, askedPushNotificationPermission } = this.props;
    const { hasNextActionAfterLogin } = this.state;

    this.setState({
      isSubmitting: true,
    });

    const loggedInUser = await dispatch(AuthActions.login, {
      phone: formValues.phone,
      password: formValues.password,
    });

    if (loggedInUser && isMobileApp && !askedPushNotificationPermission) {
      this.setState({
        showEnablePushNotificationModal: true,
      });
    } else if (!loggedInUser || !hasNextActionAfterLogin) {
      this.setState({
        isSubmitting: false,
      });
    } else {
      this._onNextLoginAction(loggedInUser.user);
    }
  }

  async _onNextLoginAction(loggedInUser) {
    const { dispatch, location, history, currentUser, tours } = this.props;
    const locationQuery = queryString.parse(location.search);

    if (locationQuery.tourRequested) {
      dispatch(TourActions.requestShowingStepSubmit, {
        listingId: parseInt(locationQuery.tourRequested, 10),
        locationQuery: locationQuery,
      });
    } else if (locationQuery.bookmarkRequested) {
      await dispatch(UserActions.addBookmark, {
        listingId: parseInt(locationQuery.bookmarkRequested, 10),
      });
      history.push('/saved');
    } else if (locationQuery.invitedBy) {
      await dispatch(postWithState, {
        action: SR_API_ACTION_ASSIGN_LEAD_APARTMENT_LOCATOR,
        payload: {
          userId: loggedInUser.id,
          assignmentContext: 'Invite Link',
          enableSmsMessage: false,
          canOverwrite: false,
          apartmentLocatorUserId: parseInt(locationQuery.invitedBy, 10),
        },
      });
    } else if (locationQuery.ownerInvited) {
      const landlordId = parseInt(locationQuery.ownerInvited, 10);
      const listingId = parseInt(locationQuery.listingId, 10);

      await dispatch(LandlordActions.assignToUser, { landlordId }).then(() => {
        return dispatch(LandlordActions.getMarketingPartnerListings, { landlordId }).then(
          (marketingPartnerListings) => {
            const marketingPartnerListing = marketingPartnerListings.find(
              (marketingPartnerListing) => marketingPartnerListing.listingId === listingId,
            );

            const redirectTo = marketingPartnerListing
              ? `/partner/${marketingPartnerListing.partner.slug}/market-property/${marketingPartnerListing.id}` // eslint-disable-line max-len
              : '/';

            history.push(redirectTo);
          },
        );
      });
    } else if (locationQuery.wantsToApply && locationQuery.listingId) {
      dispatch(HouseholdActions.roommateApplicationStep, {
        listingId: parseInt(locationQuery.listingId, 10),
      });
    } else if (locationQuery.startPrequal && locationQuery.listingId) {
      dispatch(HouseholdActions.startPrequal, {
        userId: loggedInUser.id,
        listingId: parseInt(locationQuery.listingId, 10),
      });
    } else if (locationQuery.selfTouring && locationQuery.listingId) {
      if (tours?.length === 0 || hasRoleBuyerSideAgent(currentUser.roles)) {
        history.push(`/get-started?selfTouring=true&listingId=${locationQuery.listingId}`);
      } else {
        history.push(`/listing/${locationQuery.listingId}?showSelfShowingSteps=true`);
      }
    } else if (locationQuery.joinWaitlist && locationQuery.listingId) {
      history.push(`/listing/${locationQuery.listingId}?joinWaitlist=true`);
    }
  }

  _onCloseEnablePushNotificationsModal = () => {
    const { hasNextActionAfterLogin } = this.state;

    if (hasNextActionAfterLogin) {
      this._onNextLoginAction();
    } else {
      this.setState({ showEnablePushNotificationModal: false, isSubmitting: false });
    }
  };

  render() {
    const {
      isMobileApp,
      currentUser,
      tours,
      loginApiState,
      location,
      apartmentLocators,
      isPartnerApp,
      listing,
    } = this.props;

    const { isSubmitting, showEnablePushNotificationModal } = this.state;

    const isInvitePage = location.pathname.includes('/invite');
    const locationQuery = queryString.parse(location.search);
    const signupReason = makeSignupReasonFromLocationQuery(locationQuery);
    const heroMessage = SIGNUP_REASON_TO_LOGIN_HERO_MESSAGE[signupReason];
    let headlineText = '';
    if (heroMessage) {
      headlineText = `Login or create a new account to ${heroMessage}!`;
    } else if (signupReason === SIGNUP_REASON_APARTMENT_LOCATOR_INVITE && apartmentLocators) {
      const apartmentLocatorId = parseInt(locationQuery.invitedBy, 10);
      const apartmentLocator = apartmentLocators.find(({ id }) => id === apartmentLocatorId);
      if (apartmentLocator) {
        const apartmentLocatorName = firstWordCapitalized(apartmentLocator.name);
        headlineText = `${apartmentLocatorName} invited you to search rentals on Sunroom!`;
      }
    }

    if (!isSubmitting && !currentUser.roles.includes(USER_ROLE_ANONYMOUS)) {
      const userLandingPage = getUserLandingPage({
        userRoles: currentUser.roles,
        tours: tours,
        isMobileApp: isMobileApp,
      });
      return <Redirect to={userLandingPage} />;
    }

    let contentDOM = null;
    if (isInvitePage) {
      contentDOM = (
        <div>
          <div className="loginFormContainer invitePage">
            {headlineText && <span className="headline">{headlineText}</span>}
            <SrLink
              to={isPartnerApp ? '/signup-agent' : `/get-started${location.search}`}
              className="button yellow"
              name="createAccountButton"
            >
              create new account
            </SrLink>
            <SrLink to={`/login${location.search}`} className="resetPassword">
              I already have an account. Log in now →
            </SrLink>
          </div>
        </div>
      );
    } else {
      let createNewAccountUrl = `/get-started${location.search}`;

      if (isPartnerApp) {
        createNewAccountUrl = '/signup-agent';
      } else if (listing && isListingTenantScreening(listing)) {
        createNewAccountUrl = `/start${location.search}`;
      }

      contentDOM = (
        <div className="loginFormContainer">
          {headlineText && <span className="headline">{headlineText}</span>}
          <SrLoginForm
            submitError={loginApiState.error}
            isSubmitting={loginApiState.inProgress || isSubmitting}
            onSubmit={this.onFormSubmit}
          />
          <div className="divisorContainer">
            <span className="signupLoginOrLabel">or</span>
          </div>
          <SrLink to={createNewAccountUrl} className="button yellow" name="createAccountButton">
            create new account
          </SrLink>
          <SrLink to={`/reset-password${location.search}`} className="resetPassword">
            Forgot Password?
          </SrLink>
        </div>
      );
    }

    return (
      <div className="page-login">
        <div className="loginContainer">
          <SrLogo redirectTo="/" />
          {contentDOM}
        </div>

        {showEnablePushNotificationModal && (
          <SrEnablePushNotificationsModal onClose={this._onCloseEnablePushNotificationsModal} />
        )}
      </div>
    );
  }
}

LoginPage.displayName = 'LoginPage';

export default LoginPage;
